import {
  CampoNegociacao,
  SituacaoRespostaCotacaoProduto,
  SituacoesCotacao,
  TipoNegociacao,
} from 'px-business-components';
import { CasaDecimalUtils } from 'px-components';
import {
  cloneDeep, differenceWith, isArray, isEmpty, isEqual, isNumber,
} from 'lodash';
import currency from 'currency.js';

const DEFAULT_PRECISION = CasaDecimalUtils.DEFAULT_CASAS_DECIMAIS;

function calcularValorDesconto(preco, percentualDesconto, precision) {
  return currency(preco, { precision })
    .multiply(currency(percentualDesconto, { precision })).value;
}

function aplicarDescontoAoPreco(preco, desconto, precision) {
  return currency(preco, { precision }).subtract(desconto).value;
}

function aplicarDescontoAosProdutos(negociacao, produtosOriginais) {
  return negociacao.produtos.map((produto) => {
    const precision = produto.cotacaoProduto.produto.casasDecimais || DEFAULT_PRECISION;
    const precoUnitarioOriginal = produtosOriginais.find(p => p.id === produto.id).precoUnitario;
    const desconto = calcularValorDesconto(
      precoUnitarioOriginal,
      negociacao.percentualDesconto,
      precision,
    );
    return {
      ...produto,
      precoUnitario: aplicarDescontoAoPreco(precoUnitarioOriginal, desconto, precision),
    };
  });
}

function isProdutoValido(produto) {
  return produto.situacao === SituacaoRespostaCotacaoProduto.keys.RESPONDIDA.key
    && SituacoesCotacao.ABERTAS.includes(produto.cotacaoProduto.situacao);
}

function buildProdutos(solicitacao) {
  const produtos = solicitacao.respostaCotacao.produtos.filter(produto => isProdutoValido(produto));
  return produtos.map((produto) => {
    const prod = cloneDeep(produto);
    return {
      id: prod.id,
      cotacaoProduto: {
        id: prod.cotacaoProduto.id,
        produto: prod.cotacaoProduto.produto,
        situacao: prod.cotacaoProduto.situacao,
        especificacaoProduto: prod.cotacaoProduto.especificacaoProduto,
      },
      arquivos: prod.arquivos,
      precoUnitario: prod.precoUnitario,
      especificacaoProduto: prod.especificacaoProduto,
      situacao: prod.situacao,
      ipi: prod.impostos.ipi,
      icms: prod.impostos.icms,
    };
  });
}

function buildNegociacao(solicitacao, negociacaoComprador) {
  return {
    novaFormaPagemento: solicitacao.respostaCotacao.formaPagamento,
    novoPrazoPagamento: cloneDeep(solicitacao.respostaCotacao.prazoPagamento),
    novoTipoFrete: solicitacao.respostaCotacao.frete.tipoFrete,
    novoPrazoEntrega: solicitacao.respostaCotacao.frete.prazoEntrega,
    novoTotalTransporte: solicitacao.respostaCotacao.frete.totalTransporte
      ? solicitacao.respostaCotacao.frete.totalTransporte.toFixed(2) : 0,
    produtos: buildProdutos(solicitacao),
    percentualDesconto: negociacaoComprador ? negociacaoComprador.percentualDesconto : 0,
  };
}

function getNumberToCompare(numb, precision) {
  const defaultPrecision = precision || DEFAULT_PRECISION;
  if (isNumber(numb)) {
    return numb.toFixed(defaultPrecision);
  }
  return numb;
}

function isPercent(number) {
  return isNumber(number) ? number < 1 && number >= 0 : false;
}

function percentualValue(precoComDesconto, precoOriginal, precision) {
  return currency(precoOriginal, { precision })
    .subtract(currency(precoComDesconto, { precision }))
    .divide(currency(precoOriginal, { precision }))
    .value;
}

function getPercentualDescontoNegociacao(negociacao, resposta, negociacaoComprador) {
  if (!negociacao.$$descontoAplicado) {
    return 0;
  }
  if (isEqual(negociacao.percentualDesconto, negociacaoComprador.percentualDesconto)) {
    return negociacao.percentualDesconto;
  }

  const produto = negociacao.produtos[0];
  const precision = produto.cotacaoProduto.produto.casasDecimais || DEFAULT_PRECISION;
  const precoOriginal = resposta.produtos.find(prod => prod.id === produto.id).precoUnitario;

  return percentualValue(produto.precoUnitario, precoOriginal, precision);
}

function isEqualFields(initial, negociacao) {
  const booleanMaps = Object.keys(initial).map((key) => {
    const initialField = initial[key];
    if (isArray(initialField)) {
      return true;
    }
    if (isPercent(initialField) || isPercent(negociacao[key])) {
      return isEqual(initialField, negociacao[key]);
    }
    if (isNumber(initialField) || isNumber(negociacao[key])) {
      return isEqual(getNumberToCompare(initialField), getNumberToCompare(negociacao[key]));
    }
    return isEqual(initialField, negociacao[key]);
  });
  return !booleanMaps.includes(false);
}

function getDiffProdutos(initial, other) {
  return differenceWith(initial, other, (first, sec) => first.id === sec.id
    && getNumberToCompare(first.precoUnitario, first.cotacaoProduto.produto.casasDecimais)
      === getNumberToCompare(sec.precoUnitario, sec.cotacaoProduto.produto.casasDecimais)
    && isEmpty(first.arquivos));
}

function isEqualArray(initial, other) {
  const diff = getDiffProdutos(initial, other);
  return isEmpty(diff);
}

function isEqualNegociacao(initial, negociacao) {
  return isEqualFields(initial, negociacao)
    && isEqualArray(initial.produtos, negociacao.produtos);
}

function getChangedFields(initial, negociacao) {
  return Object.keys(initial).filter((key) => {
    const initialField = initial[key];
    if (isArray(initialField)) {
      return false;
    }
    if (isPercent(initialField) && isPercent(negociacao[key])) {
      return !isEqual(initialField, negociacao[key]);
    }
    if (isNumber(initialField) || isNumber(negociacao[key])) {
      return !isEqual(getNumberToCompare(initialField), getNumberToCompare(negociacao[key]));
    }

    return !isEqual(initialField, negociacao[key]);
  });
}

function getFromChangedFields(fields, negociacao) {
  const negociacaoGeral = {};
  const camposNegociacao = [];
  const campos = CampoNegociacao.values.filter(value => fields.includes(value.newField));
  campos.forEach((campo) => {
    negociacaoGeral[campo.newField] = negociacao[campo.newField];
    camposNegociacao.push(campo.key);
  });
  negociacaoGeral.campos = camposNegociacao;
  return negociacaoGeral;
}

function checkChangeInIds(newArquivos, initialArquivos) {
  const initialIds = initialArquivos.map(initArq => initArq.id);
  return newArquivos.some(arq => !initialIds.includes(arq.id));
}

function hasChangeInArquivos(newArquivos, initialArquivos) {
  if (isEmpty(newArquivos) && isEmpty(initialArquivos)) {
    return false;
  }
  if (!isEmpty(initialArquivos) && isEmpty(newArquivos)) {
    return true;
  }
  if (!isEmpty(newArquivos) && isEmpty(initialArquivos)) {
    return true;
  }
  if (newArquivos.length !== initialArquivos.length) {
    return true;
  }
  return checkChangeInIds(newArquivos, initialArquivos);
}

function getChangedArquivos(produto, initialProdutos) {
  const produtoInitial = initialProdutos.filter(initialProduto => initialProduto.id === produto.id);
  if (!hasChangeInArquivos(produto.arquivos, produtoInitial.arquivos)) {
    return [];
  }
  return produto.arquivos;
}

function getCamposProdutos(produto, initialProdutos) {
  const initialProduto = initialProdutos.find(p => p.id === produto.id);
  const campos = [];
  if (!isEqual(
    getNumberToCompare(produto.precoUnitario),
    getNumberToCompare(initialProduto.precoUnitario),
  )) {
    campos.push(CampoNegociacao.keys.PRECO_UNITARIO.key);
  }
  if (!isEqual(produto.icms, initialProduto.icms)) {
    campos.push(CampoNegociacao.keys.ICMS.key);
  }
  if (!isEqual(produto.ipi, initialProduto.ipi)) {
    campos.push(CampoNegociacao.keys.IPI.key);
  }
  if (!isEqual(produto.especificacaoProduto, initialProduto.especificacaoProduto)) {
    campos.push(CampoNegociacao.keys.ESPECIFICACAO.key);
  }
  if (hasChangeInArquivos(produto.arquivos, initialProduto.arquivos)) {
    campos.push(CampoNegociacao.keys.ARQUIVOS_PRODUTO.key);
  }
  return campos;
}

function buildFromNegociacao(
  negociacao,
  initial,
  message,
  arquivos,
  resposta,
  negociacaoComprador,
) {
  const registro = {
    resposta: {
      id: resposta.id,
    },
    tipo: TipoNegociacao.keys.RESPOSTA.key,
    message,
    arquivos,
    percentualDesconto: getPercentualDescontoNegociacao(negociacao, resposta, negociacaoComprador),
  };

  const changedFields = getChangedFields(negociacao, initial);
  registro.negociacao = getFromChangedFields(changedFields, negociacao);

  registro.produtos = negociacao.produtos
    .map(produto => ({
      respostaProduto: {
        id: produto.id,
      },
      negociacao: {
        campos: getCamposProdutos(produto, initial.produtos),
        novoPrecoUnitario: produto.precoUnitario,
        novoArquivosProduto: getChangedArquivos(produto, initial.produtos),
        novaEspecificacaoProduto: produto.especificacaoProduto,
        novoIpi: produto.ipi,
        novoIcms: produto.icms,
      },
    })).filter(produto => !isEmpty(produto.negociacao.campos));
  return registro;
}

export {
  buildProdutos,
  buildNegociacao,
  isEqualNegociacao,
  buildFromNegociacao,
  aplicarDescontoAosProdutos,
};
